import React, { useContext, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  styled,
  Avatar,
  useTheme,
} from "@mui/material";
import { POST } from "api/index";
import CloseIcon from "@mui/icons-material/Close";
import { SubmitButton } from "ui/components/shared/SubmitButton";
import useScreenSize from "hooks/ScreenSize";
import { TextSizes, MobileWidth } from "entities/constants";
import { TextSizeContext } from "data/index";
import PersonImage from "assets/images/person.jpg";
import CropImageDialog from "./CropImageDialog";
import { useTranslation } from "react-i18next";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1.25rem",
    padding: "1.5rem",
  },
}));

const ModalContent = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1.75rem",
  padding: "1rem 0",
}));

const AvatarPreview = styled(Avatar)(() => ({
  width: "8rem",
  height: "8rem",
  border: "3px solid #e0e0e0",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const ButtonRow = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  gap: "1rem",
  marginTop: "1rem",
}));

type Props = {
  closeModal: () => void;
  imageUrl: string;
  setImageUrl: (url: string) => void;
  uploadImage: (file: File) => Promise<{ status: number; content: string }>;
};

const ImageUploadModal = ({ closeModal, imageUrl, setImageUrl, uploadImage }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [showCrop, setShowCrop] = useState(false);
  const { width } = useScreenSize();
  const theme = useTheme();
  const { state: textSize } = useContext(TextSizeContext);
  const { t } = useTranslation();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setShowCrop(true);
    }
  };

  const handleCropSave = async (blob: Blob) => {
    const imageFile = new File([blob], "profile.jpg", { type: "image/jpeg" });
    const uploadResponse = await uploadImage(imageFile);
  
    if (uploadResponse.status === 200 && uploadResponse.content) {
      const imageUrl = uploadResponse.content;
      const userToken = localStorage.getItem("currentUserToken");
      const body = new URLSearchParams();
      body.append("imageUrl", imageUrl);
      body.append("userToken", userToken || "");
      await POST("setUserImage", body);
  
      setImageUrl(imageUrl);
      localStorage.setItem("currentUserImage", imageUrl);
      window.dispatchEvent(new Event("profileImageChanged"));
  
      setShowCrop(false);
      setFile(null);
      closeModal();
    }
  };
  

  const handleReset = () => {
    setImageUrl("");
    localStorage.removeItem("currentUserImage");
    window.dispatchEvent(new Event("profileImageChanged"));
  };

  return (
    <>
      <StyledDialog open onClose={closeModal} fullWidth maxWidth="xs">
        <DialogTitle sx={{ p: 0, mb: "1rem" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {t("imageUpload.title")}
            </Typography>
            <IconButton onClick={closeModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <ModalContent>
            <Typography
              fontSize={parseInt(TextSizes[textSize].subhead)}
              color={theme.palette.text.secondary}
              textAlign="center"
              sx={{ maxWidth: "22rem" }}
            >
              {t("imageUpload.instructions")}
            </Typography>

            <AvatarPreview src={imageUrl || PersonImage} />

            <input
              type="file"
              accept="image/*"
              hidden
              ref={fileInputRef}
              onChange={handleFileSelect}
            />

            <ButtonRow>
              <SubmitButton color="primary" onClick={() => fileInputRef.current?.click()}>
                {t("imageUpload.select")}
              </SubmitButton>
              <SubmitButton color="error" onClick={handleReset}>
                {t("imageUpload.reset")}
              </SubmitButton>
            </ButtonRow>
          </ModalContent>
        </DialogContent>
      </StyledDialog>

      {file && showCrop && (
        <CropImageDialog
          file={file}
          onCancel={() => {
            setShowCrop(false);
            setFile(null);
          }}
          onCropConfirm={handleCropSave}
        />
      )}
    </>
  );
};

export default ImageUploadModal;
